import classnames from 'classnames';
import { FormattedMessage } from 'components/shared/Intl';
import { Switch } from 'components/shared/Switch/Switch';
import { FC, KeyboardEvent, useCallback } from 'react';
import styled from 'styled-components';

export interface ToggleFieldProps {
  classNames?: string;
  e2e?: string;
  id: string;
  onChange(value: boolean): void;
  value: boolean;
  isDisabled?: boolean;
  tabIndex?: number;
  switchClassName?: string;

  switchSize?: 'md' | 'sm';

  useFormControlStyle?: boolean;

  useEditedStyle?: boolean;

  withStateLabel?: boolean;
}

export const ToggleFieldWrapper = styled.div`
  height: 32px;
  cursor: pointer;

  &.disabled {
    cursor: default;
    pointer-events: none;
  }

  & > .custom-switch {
    margin-right: -0.6em;
  }
`;

export const ToggleField: FC<ToggleFieldProps> = ({
  value,
  onChange,
  isDisabled = false,
  e2e,
  id,
  classNames,
  tabIndex,
  switchClassName,
  switchSize,
  useFormControlStyle = true,
  useEditedStyle,
  withStateLabel = true,
}) => {
  const clickWrapper = useCallback(() => {
    if (isDisabled) {
      return;
    }
    onChange(!value);
  }, [onChange, isDisabled, value]);

  const keyWrapper = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === ' ') {
        clickWrapper();
      }
    },
    [clickWrapper],
  );

  return (
    <ToggleFieldWrapper
      className={classnames('d-flex flex-row align-items-center ', classNames, {
        'form-control form-control-md': useFormControlStyle,
        disabled: isDisabled,
        'text-muted': isDisabled,
        'justify-content-between': withStateLabel,
        'justify-content-end': !withStateLabel,
      })}
      data-e2e={e2e}
      onClick={clickWrapper}
      onKeyPress={keyWrapper}
      tabIndex={tabIndex}
    >
      {withStateLabel && <FormattedMessage data-testid="toggle-field-label" id={value ? 'enabled' : 'disabled'} />}
      <Switch
        id={id}
        e2e={e2e}
        isDisabled={isDisabled}
        value={value}
        className={switchClassName}
        size={switchSize}
        useEditedStyle={useEditedStyle}
      />
    </ToggleFieldWrapper>
  );
};

ToggleField.displayName = 'ToggleField';
